<script lang="ts">
	import { fade } from 'svelte/transition'
	import { portal } from 'svelte-portal'
	import 'overlayscrollbars/styles/overlayscrollbars.css'
	import OverlayScrollbar from './OverlayScrollbar.svelte'
	import { Button, ButtonType, ButtonSize, Icon } from '@repo/commons/components'
	import { disableBodyScroll, enableBodyScroll, scaleFade } from '@repo/commons/utils'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'

	const DEFAULT_MODAL_PORTAL_ID = 'modal-portal-root'
	const getPortalRoot = (id: string): HTMLElement | null =>
		typeof document === 'undefined' ? null : document.getElementById(id)

	interface Props {
		closeOnBackdropClick?: boolean
		visible?: boolean
		title?: string
		headerIcon?: IconName | null
		isFullSize?: boolean
		styleProps?: string | null
		titleStyle?: string | null
		contentStyle?: string | null
		showCloseButton?: boolean
		scrollable?: boolean
		Px?: string
		Py?: string
		portalParent?: HTMLElement | null
		className?: string | null
		onclose?: (() => void) | null
		children?: import('svelte').Snippet
		footer?: import('svelte').Snippet
		header?: import('svelte').Snippet
		headerActions?: import('svelte').Snippet
	}

	let {
		closeOnBackdropClick = true,
		visible = false,
		title = '',
		headerIcon = null,
		isFullSize = false,
		styleProps = null,
		titleStyle = null,
		contentStyle = null,
		showCloseButton = true,
		scrollable = true,
		Px = '2rem',
		Py = '2rem',
		portalParent = null,
		className = null,
		onclose = null,
		children,
		footer,
		header,
		headerActions
	}: Props = $props()

	let modalEl: HTMLDivElement | null = $state(null)
	let shouldBounce = $state(false)
	let blockBackdropClick = false
	const resolvedPortalParent = $derived.by<HTMLElement | 'body'>(
		() => portalParent ?? getPortalRoot(DEFAULT_MODAL_PORTAL_ID) ?? 'body'
	)

	const handleClose = () => {
		onclose?.()
	}

	const handleBackdropClick = () => {
		if (blockBackdropClick) return
		if (closeOnBackdropClick) {
			handleClose()
		} else {
			triggerBounce()
		}
	}

	export function triggerBounce() {
		if (!modalEl) return
		shouldBounce = true
		setTimeout(() => (shouldBounce = false), 250)
	}

	$effect(() => {
		if (visible) {
			blockBackdropClick = true
			setTimeout(() => (blockBackdropClick = false), 0)

			const scrollY = disableBodyScroll()

			const handleKey = (event: KeyboardEvent) => {
				if (event.key === 'Escape') {
					event.stopPropagation()
					if (closeOnBackdropClick) {
						handleClose()
					} else {
						triggerBounce()
					}
				}
			}

			document.addEventListener('keydown', handleKey)

			return () => {
				document.removeEventListener('keydown', handleKey)
				// Re-enable body scroll when modal is closed
				enableBodyScroll(scrollY)
			}
		}
	})
</script>

{#if visible}
	<div class="modal-wrapper" aria-hidden={!visible} use:portal={resolvedPortalParent}>
		<div
			class="modal-backdrop"
			role="presentation"
			onclick={handleBackdropClick}
			transition:fade={{ duration: 200 }}
		></div>

		<div
			bind:this={modalEl}
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
			class={`modal ${isFullSize ? 'full-size' : ''} ${className}`}
			class:full-size={isFullSize}
			class:bounce={shouldBounce}
			style={styleProps}
			transition:scaleFade={{ duration: 300, baseScale: 0.95 }}
		>
			{#if header}
				<div class="modal-header" style={`padding: ${Py} ${Px} 0 ${Px}`}>
					{@render header()}
				</div>
			{:else if title || showCloseButton || headerActions}
				<div class="modal-header" style={`padding: ${Py} ${Px} 0 ${Px}`}>
					<h2 id="modal-title" class="modal-title" style={titleStyle}>
						{#if headerIcon}
							<Icon name={headerIcon} size="md" />
						{/if}
						{title}
					</h2>

					{#if headerActions || showCloseButton}
						<div class="modal-header-actions">
							{@render headerActions?.()}
							{#if showCloseButton}
								<Button
									buttonText=""
									buttonType={ButtonType.TERTIARY_INVERSE}
									buttonSize={ButtonSize.SMALL}
									rounded
									iconProps={{ name: 'close', size: 'md', class: 'text-[#000000]' }}
									onclick={handleClose}
								/>
							{/if}
						</div>
					{/if}
				</div>
			{/if}

			{#if scrollable}
				<OverlayScrollbar>
					<div class="modal-content" style={`padding: 0 ${Px} ${Py} ${Px}; ${contentStyle || ''}`}>
						{@render children?.()}
					</div>
				</OverlayScrollbar>
			{:else}
				<div class="modal-content" style={`padding: 0 ${Px} ${Py} ${Px}; ${contentStyle || ''}`}>
					{@render children?.()}
				</div>
			{/if}

			{#if footer}
				<div class="modal-footer" style={`padding: ${Py} ${Px} ${Py} ${Px}`}>
					{@render footer()}
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.modal-wrapper {
		@apply fixed top-0 left-0 right-0 bottom-0 z-50 flex justify-center items-center;
	}

	.modal-backdrop {
		@apply absolute top-0 left-0 right-0 bottom-0 bg-[#002C4D] bg-opacity-60;
	}

	.modal {
		@apply relative bg-white flex flex-col outline-none rounded-2xl overflow-hidden;
		min-width: 300px;
		max-width: 80vw;
		max-height: 90vh;
		border: 1px solid rgba(0, 58, 102, 0.1);
		box-shadow:
			0px 8px 8px -4px rgba(0, 0, 0, 0.04),
			0px 20px 24px -4px rgba(0, 0, 0, 0.08);
		transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
	}

	.modal.bounce {
		transform: scale(1.03);
	}

	.full-size {
		@apply w-[100vw] h-[100vh] rounded-none;
		max-width: 100vw;
		max-height: 100vh;
	}

	.modal-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1.5rem;
	}

	.modal-header-actions {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	.modal-title {
		@apply flex text-[24px] font-semibold leading-normal;
		gap: 10px;
		/*font-size: 18px;*/
		/*line-height: 26px;*/
		/*font-weight: 700;*/
		/*display: flex;*/
		/*align-items: center;*/
	}

	.modal-content {
		@apply flex-grow;
	}

	.modal-content::-webkit-scrollbar {
		width: 6px;
	}

	.modal-content::-webkit-scrollbar-track {
		background: transparent;
	}

	.modal-content::-webkit-scrollbar-thumb {
		@apply bg-brand border-2;
	}

	.modal-footer {
		@apply flex-shrink-0 border-t border-gray-200;
	}
</style>
